import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ReactCrop, { centerCrop, makeAspectCrop, type Crop, type PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useCreatePostData } from '@wlb/shared/context/CreatePostContext';
import { useProfile } from '@wlb/shared/context/ProfileContext';
import { POST_FEELINGS, POST_GALLERY_MAX_IMAGES, POST_IMAGE_LAYOUT, POST_MAX_CHAR_COUNT, POST_MAX_IMAGE_SIZE_BYTES, POST_MEDIA_TYPE, POST_PRIVACY, SUPPORTED_IMAGE_MIME_TYPES, SUPPORTED_VIDEO_MIME_TYPES, isSupportedImageFormat } from '@wlb/shared/constants';
import type { MediaPickResponse, PostPrivacy } from '@wlb/shared/types';
import type { MediaFile, PostImageLayout } from '@wlb/shared/types/post';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { ToastService } from '@wlb/shared/platform/notifier';
import { getProfileAvatarUrl } from '../features/profile/useCurrentProfile';
import { GroupImage } from '../features/groups/GroupImage';
import { Avatar } from '../components/ui/Avatar';
import { Button } from '../components/ui/Button';
import { BuddiesIcon, ChevronDownIcon, ChevronLeftIcon, CloseIcon, DocumentIcon, GlobeIcon, GroupsTabIcon, LockIcon, PlusIcon, PostFeelingIcon, PostGifIcon, PostHashtagIcon, PostImageIcon, PostVideoIcon, SearchIcon, SmileIcon } from '../components/ui/icons';
import { GifPicker } from '../components/ui/GifPicker';
import { EmojiPicker } from '../components/ui/EmojiPicker';
import type { GifSelection } from '@wlb/shared/types/gif';
import { ImageLayoutModal } from '../features/createPost/ImageLayoutModal';
import { CompareImagesModal } from '../features/createPost/CompareImagesModal';
import {
  createPostReturnState,
  resolveCreatePostReturnPath,
  resolvePostSubmitReturnPath,
  type CreatePostGroupTarget,
  type CreatePostRouteState,
} from '../features/createPost/createPostRoute';
import {
  FIRST_POST_MIN_LENGTH,
  FIRST_POST_PROMPTS,
  isMeaningfulFirstPost,
  type FirstPostPromptKey,
} from '../features/createPost/firstPostPrompts';
import { insertPostText } from '../features/createPost/postTextInsertion';
import { getUsernameDisplay } from '../utils/usernameDisplay';
import './createPost.css';

const PRIVACY_OPTIONS: PostPrivacy[] = [POST_PRIVACY.PUBLIC, POST_PRIVACY.BUDDIES_ONLY, POST_PRIVACY.ONLY_ME];

const SUGGESTED_HASHTAGS = ['Progress', 'Accountability', 'Support', 'MealPrep', 'WLB', 'Workout', 'Walking'];

/**
 * Post media renders in a 16:9 box with `object-fit: cover`, which slices the top and
 * bottom off a square circular crop. Exporting the circle centred on a transparent
 * 16:9 canvas keeps the whole circle visible without changing how other media renders.
 */
const CIRCLE_CROP_EXPORT_ASPECT = 16 / 9;

/**
 * Only a CORS-negotiated cross-origin image keeps the export canvas untainted, and
 * requesting CORS for a host that does not answer with it fails the load outright,
 * so the crop preview falls back to a plain load when the CORS attempt errors.
 */
const isCrossOriginMedia = (uri?: string | null): boolean => {
  if (!uri || uri.startsWith('blob:') || uri.startsWith('data:')) return false;
  try {
    return new URL(uri, window.location.href).origin !== window.location.origin;
  } catch {
    return false;
  }
};

const PrivacyIcon = ({ privacy, size }: { privacy: PostPrivacy; size: number }) => {
  if (privacy === POST_PRIVACY.PUBLIC) return <GlobeIcon size={size} />;
  if (privacy === POST_PRIVACY.BUDDIES_ONLY) return <BuddiesIcon size={size} />;
  return <LockIcon size={size} />;
};

export function CreatePost() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { updateProfileLocal } = useProfile();

  const autoGrowTextarea = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 300)}px`;
  }, []);

  const [privacyOpen, setPrivacyOpen] = useState(false);
  // Photo posts start with "how do you want to share?", never the file picker.
  const [layoutPickerOpen, setLayoutPickerOpen] = useState(false);
  const [compareLayout, setCompareLayout] = useState<PostImageLayout | null>(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [crop, setCrop] = useState<Crop>();
  const [cropAspect, setCropAspect] = useState<'original' | 'square' | 'portrait'>('square');
  const [cropShape, setCropShape] = useState<'rectangle' | 'circle'>('rectangle');
  const [cropping, setCropping] = useState(false);
  const [cropCorsFallback, setCropCorsFallback] = useState(false);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [feelingSearch, setFeelingSearch] = useState('');
  const cropImageRef = useRef<HTMLImageElement>(null);
  const {
    profile,
    content,
    privacy,
    selectedHashtags,
    selectedFeeling,
    media,
    mediaFiles,
    imageLayout,
    loading,
    isEditMode,
    activeSheet,
    showDiscardModal,
    canSubmit,
    hasUnsavedChanges,
    handleContentChange,
    handleAddHashtag,
    handleRemoveHashtag,
    handleSelectFeeling,
    openSheet,
    closeSheet,
    handleMediaPicked,
    handleRemoveMedia,
    handleRemoveMediaAt,
    setCompareImages,
    handleSelectPrivacy,
    promptDiscard,
    handleKeepEditing,
    handleDiscardPost,
    handleSubmit,
    handleBackToFeed,
    loadPostForEdit,
    resetForm,
    editingPost,
  } = useCreatePostData();

  useEffect(() => { autoGrowTextarea(); }, [content, autoGrowTextarea]);
  useEffect(() => {
    if (activeSheet) setEmojiPickerOpen(false);
    if (activeSheet !== 'feeling') setFeelingSearch('');
  }, [activeSheet]);

  const routeState = location.state as CreatePostRouteState | null;
  // Both survive the state being dropped below, so a submit still knows where it
  // was opened from after the effect replaces the entry.
  const [routeGroup] = useState<CreatePostGroupTarget | null>(() => routeState?.group ?? null);
  const [returnTo] = useState<string | null>(() => routeState?.returnTo ?? null);
  const [returnScrollY] = useState<number | null>(() => routeState?.returnScrollY ?? null);
  const [initialIntent] = useState(() =>
    routeState?.editPost ? null : routeState?.intent ?? null,
  );

  /**
   * CreatePostProvider lives in AppShell for the whole session, so composer
   * state — including `editingPostId` — survives leaving this page. Decide the
   * mode once per visit, and drop the router state so a later plain visit to
   * /create does not reopen edit mode and overwrite the post that was edited.
   */
  const modeResolvedRef = useRef(false);
  useEffect(() => {
    if (modeResolvedRef.current) return;
    modeResolvedRef.current = true;
    if (routeState?.editPost) {
      loadPostForEdit(routeState.editPost);
    } else {
      resetForm();
      if (routeState?.initialMedia) {
        handleMediaPicked({ success: true, asset: routeState.initialMedia });
      }
    }
    if (routeState) navigate(location.pathname, { replace: true, state: null });
  }, [
    routeState,
    loadPostForEdit,
    resetForm,
    handleMediaPicked,
    navigate,
    location.pathname,
  ]);

  const initialIntentHandledRef = useRef(false);
  useEffect(() => {
    if (
      !modeResolvedRef.current ||
      initialIntentHandledRef.current ||
      !initialIntent
    ) {
      return;
    }
    initialIntentHandledRef.current = true;
    if (initialIntent === 'text') {
      textareaRef.current?.focus();
      return;
    }
    if (initialIntent === 'feeling') {
      openSheet('feeling');
      return;
    }
    if (initialIntent === 'photo') {
      setLayoutPickerOpen(true);
    }
  }, [initialIntent, openSheet]);

  /**
   * A post created from a group is locked to that group (mobile parity): the
   * group comes from the route, or from the post being edited when the composer
   * is reopened for an existing group post.
   */
  const groupTarget = useMemo<CreatePostGroupTarget | null>(() => {
    if (routeGroup?.id) return routeGroup;
    if (editingPost?.groupId) {
      return { id: String(editingPost.groupId), name: editingPost.groupName ?? null };
    }
    return null;
  }, [routeGroup, editingPost]);
  const mandatoryFirstPost =
    profile?.isNewUser === true && !isEditMode && groupTarget === null;
  const [firstPostPrompt, setFirstPostPrompt] =
    useState<FirstPostPromptKey | null>(null);
  const hasFirstPostMedia = media !== null || mediaFiles.length > 0;
  const canSubmitFirstPost =
    !mandatoryFirstPost || isMeaningfulFirstPost(content, hasFirstPostMedia);
  const firstPostPlaceholder = firstPostPrompt
    ? t(`home.newUserWelcome.prompts.${firstPostPrompt}.placeholder`)
    : t('home.newUserWelcome.choosePromptPlaceholder');
  const firstPostGuidance = canSubmitFirstPost
    ? t('home.newUserWelcome.readyHint')
    : hasFirstPostMedia && content.trim().length === 0
      ? t('home.newUserWelcome.mediaMessageHint')
      : t('home.newUserWelcome.minimumLengthHint', {
          count: FIRST_POST_MIN_LENGTH,
        });

  const [hashtagInput, setHashtagInput] = useState('');
  const profileAvatarUrl = getProfileAvatarUrl(profile);
  const profileName = getUsernameDisplay(profile, t('home.rightBanner.guestName'));
  // Group posts are authored by the group, so the composer header shows it.
  const avatarUrl = groupTarget ? groupTarget.image ?? '' : profileAvatarUrl;
  const displayName = groupTarget ? groupTarget.name?.trim() || t('groupDetails.title') : profileName;
  const returnPath = resolveCreatePostReturnPath(returnTo, groupTarget);
  const feeling = POST_FEELINGS.find(item => item.key === selectedFeeling);
  const filteredFeelings = useMemo(() => {
    const query = feelingSearch.trim().toLocaleLowerCase();
    if (!query) return POST_FEELINGS;
    return POST_FEELINGS.filter(item =>
      t(`home.createPost.feeling.${item.key}`).toLocaleLowerCase().includes(query),
    );
  }, [feelingSearch, t]);
  const privacyLabel = useMemo(() => {
    if (privacy === POST_PRIVACY.BUDDIES_ONLY) return t('home.createPost.privacy.buddiesOnly');
    if (privacy === POST_PRIVACY.ONLY_ME) return t('home.createPost.privacy.onlyMe');
    return t('home.createPost.privacy.public');
  }, [privacy, t]);

  const characterProgress = Math.min(100, Math.round((content.length / POST_MAX_CHAR_COUNT) * 100));

  const submitHashtag = () => {
    const normalized = hashtagInput.replace(/^#+/, '').trim();
    if (!normalized) return;
    handleAddHashtag(normalized);
    setHashtagInput('');
  };

  const handleEmojiSelect = (emoji: string) => {
    const textarea = textareaRef.current;
    const start = textarea?.selectionStart ?? content.length;
    const end = textarea?.selectionEnd ?? start;
    const insertion = insertPostText({
      content,
      insertedText: emoji,
      selectionStart: start,
      selectionEnd: end,
      maxLength: POST_MAX_CHAR_COUNT,
    });
    if (!insertion) return;

    handleContentChange(insertion.content);
    window.requestAnimationFrame(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(
        insertion.cursorPosition,
        insertion.cursorPosition,
      );
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const isVideo = file.type.startsWith('video/');
    if (!isVideo && !isSupportedImageFormat(file.type, file.name)) {
      ToastService.show({ type: 'error', message: t('home.createPost.media.invalidImageFormat') });
      return;
    }
    const mediaType = isVideo ? POST_MEDIA_TYPE.VIDEO : file.type === 'image/gif' ? POST_MEDIA_TYPE.GIF : POST_MEDIA_TYPE.IMAGE;
    const response: MediaPickResponse = {
      success: true,
      asset: {
        uri: URL.createObjectURL(file),
        file,
        fileName: file.name,
        fileSize: file.size,
        type: file.type,
        mediaType,
      },
    };
    handleMediaPicked(response);
  };

  const handleGalleryFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (!files.length) return;
    const validImages = files.filter(file => isSupportedImageFormat(file.type, file.name));
    if (validImages.length !== files.length) {
      ToastService.show({ type: 'error', message: t('home.createPost.media.invalidImageFormat') });
      if (!validImages.length) return;
    }
    const oversized = validImages.find(file => file.size > POST_MAX_IMAGE_SIZE_BYTES);
    if (oversized) {
      ToastService.show({ type: 'error', message: t('home.createPost.media.imageTooLarge', { size: 10 }) });
      return;
    }
    const newFiles: MediaFile[] = validImages.map(file => ({
      uri: URL.createObjectURL(file),
      file,
      fileName: file.name,
      fileSize: file.size,
      type: file.type || 'image/jpeg',
      mediaType: POST_MEDIA_TYPE.IMAGE,
    }));
    const combined = [...albumImages, ...newFiles];
    if (combined.length > POST_GALLERY_MAX_IMAGES) {
      ToastService.show({
        type: 'error',
        message: t('home.createPost.media.galleryMaxImages', { max: POST_GALLERY_MAX_IMAGES }),
      });
      return;
    }
    setCompareImages(POST_IMAGE_LAYOUT.GALLERY, combined);
  };

  /**
   * Layout first, picker second: Single Photo opens the image picker, Gallery
   * opens a multi-select picker, the compare layouts open the slot picker.
   */
  const handleSelectImageLayout = (layout: PostImageLayout) => {
    setLayoutPickerOpen(false);
    if (layout === POST_IMAGE_LAYOUT.SINGLE) {
      photoInputRef.current?.click();
      return;
    }
    if (layout === POST_IMAGE_LAYOUT.GALLERY) {
      galleryInputRef.current?.click();
      return;
    }
    setCompareLayout(layout);
  };

  /**
   * A published post keeps its media: the likes and comments it collected belong
   * to what people saw. Editing stays open for text, feeling and hashtags, so the
   * preview is shown without any control that could change, add or drop media.
   */
  const mediaLocked = isEditMode;

  const albumImages = mediaFiles.filter(
    file =>
      !!file?.uri &&
      file.mediaType !== POST_MEDIA_TYPE.VIDEO &&
      file.mediaType !== POST_MEDIA_TYPE.GIF,
  );
  const isGallery = imageLayout === POST_IMAGE_LAYOUT.GALLERY;
  const hasAlbum = albumImages.length > 1 && !isGallery;
  const hasGallery = albumImages.length > 1 && isGallery;
  const albumLayout: PostImageLayout =
    imageLayout === POST_IMAGE_LAYOUT.COMPARISON_4 || (albumImages.length > 2 && !isGallery)
      ? POST_IMAGE_LAYOUT.COMPARISON_4
      : POST_IMAGE_LAYOUT.COMPARISON_2;

  const albumSlotLabel = (index: number): string =>
    albumLayout === POST_IMAGE_LAYOUT.COMPARISON_2
      ? index === 0
        ? t('home.createPost.media.compareBefore')
        : t('home.createPost.media.compareAfter')
      : t('home.createPost.media.compareSlotLabel', { index: index + 1 });

  const handleGifSelect = (gif: GifSelection) => {
    handleMediaPicked({
      success: true,
      asset: { uri: gif.uri, fileName: gif.fileName, type: gif.type, mediaType: POST_MEDIA_TYPE.GIF },
    });
    closeSheet();
  };

  const cropAspectValue = cropAspect === 'square' || cropShape === 'circle' ? 1 : cropAspect === 'portrait' ? 4 / 5 : undefined;
  const effectiveCropAspect: 'original' | 'square' | 'portrait' =
    cropShape === 'circle' ? 'square' : cropAspect;
  const requestCropImageCors = !cropCorsFallback && isCrossOriginMedia(media?.uri);

  const getInitialCrop = (image: HTMLImageElement, aspect = cropAspectValue): Crop => {
    const baseCrop = { unit: '%', width: 90, height: 90 } as const;
    if (!aspect) return { ...baseCrop, x: 5, y: 5 };
    return centerCrop(makeAspectCrop(baseCrop, aspect, image.width, image.height), image.width, image.height);
  };

  const handleCropImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    cropImageRef.current = event.currentTarget;
    setCrop(getInitialCrop(event.currentTarget));
  };

  const changeCropAspect = (aspect: 'original' | 'square' | 'portrait') => {
    if (cropShape === 'circle' && aspect !== 'square') {
      return;
    }
    setCropAspect(aspect);
    if (cropImageRef.current) setCrop(getInitialCrop(cropImageRef.current, aspect === 'square' ? 1 : aspect === 'portrait' ? 4 / 5 : undefined));
  };

  const openCrop = () => {
    setCropAspect('square');
    setCropShape('rectangle');
    setCropCorsFallback(false);
    setCrop(undefined);
    setCropOpen(true);
  };

  const getSourceCropInPixels = (selectedCrop: Crop, image: HTMLImageElement): PixelCrop => {
    if (selectedCrop.unit === '%') {
      return {
        unit: 'px',
        x: Math.round((selectedCrop.x / 100) * image.naturalWidth),
        y: Math.round((selectedCrop.y / 100) * image.naturalHeight),
        width: Math.max(1, Math.round((selectedCrop.width / 100) * image.naturalWidth)),
        height: Math.max(1, Math.round((selectedCrop.height / 100) * image.naturalHeight)),
      };
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    return {
      unit: 'px',
      x: Math.round(selectedCrop.x * scaleX),
      y: Math.round(selectedCrop.y * scaleY),
      width: Math.max(1, Math.round(selectedCrop.width * scaleX)),
      height: Math.max(1, Math.round(selectedCrop.height * scaleY)),
    };
  };

  const applyCrop = async () => {
    if (!media || media.mediaType !== POST_MEDIA_TYPE.IMAGE || !crop || !cropImageRef.current) return;
    setCropping(true);
    try {
      const image = cropImageRef.current;
      const sourceCrop = getSourceCropInPixels(crop, image);
      const isCircle = cropShape === 'circle';
      const diameter = Math.min(sourceCrop.width, sourceCrop.height);
      const canvas = document.createElement('canvas');
      canvas.width = isCircle ? Math.round(diameter * CIRCLE_CROP_EXPORT_ASPECT) : sourceCrop.width;
      canvas.height = isCircle ? diameter : sourceCrop.height;
      const context = canvas.getContext('2d');
      if (!context) return;
      const offsetX = isCircle ? Math.round((canvas.width - diameter) / 2) : 0;
      context.drawImage(
        image,
        sourceCrop.x,
        sourceCrop.y,
        sourceCrop.width,
        sourceCrop.height,
        offsetX,
        0,
        isCircle ? diameter : canvas.width,
        isCircle ? diameter : canvas.height,
      );
      if (isCircle) {
        context.globalCompositeOperation = 'destination-in';
        context.beginPath();
        context.arc(offsetX + diameter / 2, diameter / 2, diameter / 2, 0, Math.PI * 2);
        context.fill();
      }
      const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, isCircle ? 'image/png' : 'image/jpeg', 0.9));
      if (!blob) return;
      const fileExtension = isCircle ? 'png' : 'jpg';
      const file = new File([blob], `cropped-image.${fileExtension}`, { type: isCircle ? 'image/png' : 'image/jpeg' });
      handleMediaPicked({ success: true, asset: { ...media, uri: URL.createObjectURL(file), file, fileName: file.name, fileSize: file.size, type: file.type } });
      setCropOpen(false);
    } catch {
      ToastService.show({ type: 'error', message: t('home.createPost.media.cropFailed') });
    } finally {
      setCropping(false);
    }
  };

  const submitPost = async () => {
    const wasEditing = isEditMode;
    const success = await handleSubmit(
      groupTarget
        ? { groupId: groupTarget.id, groupName: groupTarget.name ?? undefined }
        : undefined,
    );
    if (success) {
      const completedMandatoryFirstPost = mandatoryFirstPost;
      if (completedMandatoryFirstPost) {
        await updateProfileLocal({ isNewUser: false });
      }
      const submitReturnPath = resolvePostSubmitReturnPath(
        returnPath,
        completedMandatoryFirstPost,
      );
      handleBackToFeed(() =>
        navigate(submitReturnPath, {
          state: createPostReturnState(
            completedMandatoryFirstPost ? null : returnScrollY,
            completedMandatoryFirstPost,
          ),
        }),
      );
      ToastService.show({
        type: 'success',
        message: wasEditing
          ? t('home.createPost.successToast.updated')
          : t('home.createPost.successToast.shared'),
      });
    }
  };

  const handleClose = () => {
    if (mandatoryFirstPost) return;
    if (hasUnsavedChanges) {
      promptDiscard();
      return;
    }
    navigate(-1);
  };

  const discardAndLeave = () => handleDiscardPost(() => navigate(-1));

  return (
    <main className="create-post-page" aria-labelledby="create-post-title">
      <div className="create-post-page__header">
        {mandatoryFirstPost ? (
          <span className="create-post-page__header-spacer" aria-hidden="true" />
        ) : (
          <button className="create-post-page__close" type="button" onClick={handleClose} aria-label={t('home.createPost.discardCancel')}>
            <CloseIcon size={22} />
          </button>
        )}
        <h1 id="create-post-title">{isEditMode ? t('home.createPost.editTitle') : t('home.createPost.title')}</h1>
        <span className="create-post-page__header-spacer" aria-hidden="true" />
      </div>

      <section className="create-post-card" aria-label={t('home.createPost.title')}>
        <div className="create-post-card__author">
          {groupTarget ? (
            <GroupImage
              src={avatarUrl}
              imageClassName="create-post-card__avatar"
              fallbackClassName="create-post-card__avatar"
              fallback={<GroupsTabIcon size={22} />}
            />
          ) : (
            <Avatar
              src={avatarUrl}
              name={displayName}
              username={profile?.userName}
              seed={profile?.userGuid ?? profile?.userId}
              size="lg"
              className="create-post-card__avatar"
            />
          )}
          <div>
            <strong>{displayName}</strong>
            <div className="create-post-card__privacy-wrap">
              <button
                className="create-post-card__privacy"
                type="button"
                aria-haspopup="menu"
                aria-expanded={privacyOpen}
                onClick={() => setPrivacyOpen(value => !value)}
              >
                <PrivacyIcon privacy={privacy} size={16} />
                {privacyLabel}
                <ChevronDownIcon size={16} />
              </button>
              {privacyOpen && (
                <div className="create-post-card__privacy-menu" role="menu">
                  {PRIVACY_OPTIONS.map(option => {
                    const isPublic = option === POST_PRIVACY.PUBLIC;
                    const isBuddies = option === POST_PRIVACY.BUDDIES_ONLY;
                    return (
                      <button
                        type="button"
                        role="menuitemradio"
                        aria-checked={privacy === option}
                        className={privacy === option ? 'selected' : ''}
                        key={option}
                        onClick={() => { handleSelectPrivacy(option); setPrivacyOpen(false); }}
                      >
                        <PrivacyIcon privacy={option} size={18} />
                        <span>
                          <strong>{isPublic ? t('home.privacyInfo.post.publicTitle') : isBuddies ? t('home.privacyInfo.post.buddiesTitle') : t('home.privacyInfo.post.onlyMeTitle')}</strong>
                          <small>{isPublic ? t('home.privacyInfo.post.publicDesc') : isBuddies ? t('home.privacyInfo.post.buddiesDesc') : t('home.privacyInfo.post.onlyMeDesc')}</small>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {mandatoryFirstPost && (
          <section
            className="create-post-first"
            aria-labelledby="create-post-first-title"
          >
            <h2 id="create-post-first-title">
              {t('home.newUserWelcome.guidedTitle')}
            </h2>
            <p>{t('home.newUserWelcome.guidedSubtitle')}</p>
            <fieldset>
              <legend>{t('home.newUserWelcome.promptLegend')}</legend>
              <div className="create-post-first__prompts">
                {FIRST_POST_PROMPTS.map(prompt => (
                  <button
                    type="button"
                    key={prompt.key}
                    className={
                      firstPostPrompt === prompt.key ? 'selected' : undefined
                    }
                    aria-pressed={firstPostPrompt === prompt.key}
                    onClick={() => {
                      setFirstPostPrompt(prompt.key);
                      window.requestAnimationFrame(() =>
                        textareaRef.current?.focus(),
                      );
                    }}
                    disabled={loading}
                  >
                    <span aria-hidden="true">{prompt.emoji}</span>
                    {t(`home.newUserWelcome.prompts.${prompt.key}.label`)}
                  </button>
                ))}
              </div>
            </fieldset>
          </section>
        )}

        <div className="create-post-card__editor">
          <textarea
            ref={textareaRef}
            className="create-post-card__textarea"
            value={content}
            onChange={event => handleContentChange(event.target.value)}
            onFocus={autoGrowTextarea}
            placeholder={
              mandatoryFirstPost
                ? firstPostPlaceholder
                : t('home.createPost.placeholder')
            }
            maxLength={POST_MAX_CHAR_COUNT}
            disabled={loading}
            aria-label={
              mandatoryFirstPost
                ? firstPostPlaceholder
                : t('home.createPost.placeholder')
            }
            aria-describedby={
              mandatoryFirstPost ? 'create-post-first-guidance' : undefined
            }
          />
          <button
            type="button"
            className="create-post-card__emoji-toggle"
            onClick={() => setEmojiPickerOpen(open => !open)}
            aria-label={t('comments.emojiPicker')}
            aria-expanded={emojiPickerOpen}
            aria-controls="create-post-emoji-picker"
          >
            <SmileIcon size={22} />
          </button>
          {emojiPickerOpen && (
            <div
              id="create-post-emoji-picker"
              className="create-post-card__emoji-panel"
            >
              <EmojiPicker onSelect={handleEmojiSelect} />
            </div>
          )}
        </div>
        {mandatoryFirstPost && (
          <p
            id="create-post-first-guidance"
            className={`create-post-first__guidance${
              canSubmitFirstPost ? ' create-post-first__guidance--ready' : ''
            }`}
            aria-live="polite"
          >
            {firstPostGuidance}
          </p>
        )}

        {(feeling || selectedHashtags.length > 0) && (
          <div className="create-post-card__chips" aria-label={t('home.createPost.addTagsLabel')}>
            {feeling && (
              <button
                className="create-post-card__chip create-post-card__chip--feeling"
                type="button"
                onClick={() => handleSelectFeeling(null)}
                aria-label={t('home.createPost.feelingLabel', { feeling: t(`home.createPost.feeling.${feeling.key}`) })}
              >
                <span aria-hidden="true">{feeling.emoji}</span>
                {t(`home.createPost.feeling.${feeling.key}`)}
                <CloseIcon size={16} />
              </button>
            )}
            {selectedHashtags.map(tag => (
              <button
                className="create-post-card__chip"
                type="button"
                key={tag}
                onClick={() => handleRemoveHashtag(tag)}
                aria-label={t('home.createPost.hashtags.removeCustom', { tag })}
              >
                #{tag}
                <CloseIcon size={16} />
              </button>
            ))}
          </div>
        )}

        {hasGallery ? (
          <div className="create-post-card__gallery">
            <ul className="create-post-card__gallery-grid">
              {albumImages.map((file, index) => (
                <li key={`${file.uri}-${index}`}>
                  <img src={file.uri} alt="" />
                  {!mediaLocked && (
                    <button
                      type="button"
                      className="create-post-card__gallery-remove"
                      onClick={() => handleRemoveMediaAt(index)}
                      aria-label={t('home.createPost.media.compareRemovePhoto')}
                    >
                      <CloseIcon size={16} />
                    </button>
                  )}
                </li>
              ))}
              {!mediaLocked && (
                <li>
                  <button
                    type="button"
                    className="create-post-card__gallery-add"
                    onClick={() => galleryInputRef.current?.click()}
                    aria-label={t('home.createPost.media.compareAddPhoto')}
                  >
                    <PlusIcon size={24} />
                  </button>
                </li>
              )}
            </ul>
          </div>
        ) : hasAlbum ? (
          <div className="create-post-card__album">
            <ul className={`create-post-card__album-grid create-post-card__album-grid--${albumLayout === POST_IMAGE_LAYOUT.COMPARISON_4 ? 'four' : 'two'}`}>
              {albumImages.map((file, index) => (
                <li key={`${file.uri}-${index}`}>
                  <img src={file.uri} alt="" />
                  <span className="create-post-card__album-label">{albumSlotLabel(index)}</span>
                  {!mediaLocked && (
                    <button
                      type="button"
                      className="create-post-card__album-remove"
                      onClick={() => handleRemoveMediaAt(index)}
                      aria-label={t('home.createPost.media.compareRemovePhoto')}
                    >
                      <CloseIcon size={16} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
            {!mediaLocked && (
              <button
                type="button"
                className="create-post-card__album-edit"
                onClick={() => setCompareLayout(albumLayout)}
              >
                {t('home.createPost.media.editCompare')}
              </button>
            )}
          </div>
        ) : media ? (
          <div className={`create-post-card__media${media.mediaType === POST_MEDIA_TYPE.GIF ? ' create-post-card__media--gif' : ''}`}>
            {media.mediaType === POST_MEDIA_TYPE.VIDEO ? <video src={media.uri} controls aria-label={t('home.createPost.media.preview')} /> : <img src={media.uri} alt={t('home.createPost.media.preview')} />}
            {!mediaLocked && media.mediaType === POST_MEDIA_TYPE.IMAGE && <button className="create-post-card__media-action" type="button" onClick={openCrop}>{t('home.createPost.media.crop')}</button>}
            {!mediaLocked && media.mediaType === POST_MEDIA_TYPE.GIF && <button className="create-post-card__media-action" type="button" onClick={() => openSheet('gif')}>{t('home.createPost.media.changeGif')}</button>}
            {!mediaLocked && <button type="button" onClick={handleRemoveMedia} aria-label={t('common.buttons.close')}>×</button>}
          </div>
        ) : null}

        {mediaLocked && (media || albumImages.length > 0) && (
          <p className="create-post-card__media-locked">{t('home.createPost.media.lockedOnEdit')}</p>
        )}

        <div className="create-post-card__toolbar">
          <div className="create-post-card__actions">
            {!mediaLocked && (
              <>
                <button type="button" className="create-post-card__action create-post-card__action--image" onClick={() => setLayoutPickerOpen(true)} aria-haspopup="dialog" aria-label={t('modals.uploadProfilePhoto.chooseGallery')}><PostImageIcon size={20} /></button>
                <button type="button" className="create-post-card__action create-post-card__action--video" onClick={() => fileInputRef.current?.click()} aria-label={t('home.createPost.media.chooseVideo')}><PostVideoIcon size={20} /></button>
              </>
            )}
            <button type="button" className="create-post-card__action create-post-card__action--feeling" onClick={() => openSheet('feeling')} aria-label={t('home.createPost.feeling.title')}><PostFeelingIcon size={20} /></button>
            {!mediaLocked && <button type="button" className="create-post-card__action create-post-card__action--gif" onClick={() => openSheet('gif')} aria-label={t('individualChat.attachment.gif')}><PostGifIcon size={20} /></button>}
            <button type="button" className="create-post-card__action create-post-card__action--tag" onClick={() => openSheet('hashtags')} aria-label={t('home.createPost.hashtags.title')}><PostHashtagIcon size={20} /></button>
          </div>
          <div className="create-post-card__counter">
            <span className="create-post-card__count">{t('home.createPost.characterCount', { current: content.length, max: POST_MAX_CHAR_COUNT })}</span>
            <span className="create-post-card__count-track" aria-hidden="true">
              <span className="create-post-card__count-fill" style={{ width: `${characterProgress}%` }} />
            </span>
          </div>
        </div>
        {/* No file inputs in the DOM while editing: nothing to click, and nothing a
            file drop could land on. */}
        {!mediaLocked && (
          <>
            <input ref={fileInputRef} className="create-post-page__file-input" type="file" accept={[...SUPPORTED_IMAGE_MIME_TYPES, ...SUPPORTED_VIDEO_MIME_TYPES].join(',')} onChange={handleFileChange} />
            <input ref={photoInputRef} className="create-post-page__file-input" type="file" accept={SUPPORTED_IMAGE_MIME_TYPES.join(',')} onChange={handleFileChange} />
            <input ref={galleryInputRef} className="create-post-page__file-input" type="file" accept={SUPPORTED_IMAGE_MIME_TYPES.join(',')} multiple onChange={handleGalleryFileChange} />
          </>
        )}
      </section>

      <aside className="create-post-guidelines">
        <span className="create-post-guidelines__icon" aria-hidden="true">
          <DocumentIcon size={20} />
        </span>
        <p>
          {t('home.createPost.guidelinesNoticePrefix')}
          <Link
            to="/support/community-guidelines"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('home.createPost.guidelinesNoticeLink')}
          </Link>
          {t('home.createPost.guidelinesNoticeSuffix')}
        </p>
      </aside>

      {!mandatoryFirstPost && (
        <aside className="create-post-tip">
          <span className="create-post-tip__icon" aria-hidden="true">✦</span>
          <div><strong>{t('home.createPost.writingTipTitle')}</strong><p>{t('home.createPost.writingTipBody')}</p></div>
        </aside>
      )}

      <div className="create-post-page__footer">
        <Button fullWidth size="lg" onClick={() => { submitPost().catch(() => undefined); }} disabled={!canSubmit || !canSubmitFirstPost || loading}>
          {loading ? t('home.createPost.creating') : isEditMode ? t('home.createPost.updateButton') : t('home.createPost.submitButton')}
        </Button>
      </div>

      {layoutPickerOpen && (
        <ImageLayoutModal
          selectedLayout={media ? imageLayout : null}
          onSelect={handleSelectImageLayout}
          onClose={() => setLayoutPickerOpen(false)}
        />
      )}
      {compareLayout && (
        <CompareImagesModal layout={compareLayout} onClose={() => setCompareLayout(null)} />
      )}

      {activeSheet && <div className="create-post-sheet__backdrop" role="presentation" onClick={closeSheet} />}
      {activeSheet === 'feeling' && (
        <section className="create-post-sheet create-post-sheet--panel create-post-sheet--feelings" role="dialog" aria-modal="true" aria-labelledby="create-post-feeling-title">
          <header className="create-post-sheet__header">
            <button className="create-post-sheet__back" type="button" onClick={closeSheet} aria-label={t('common.buttons.back')}><ChevronLeftIcon size={20} /></button>
            <h2 id="create-post-feeling-title">{t('home.createPost.feeling.title')}</h2>
            <button className="create-post-sheet__close" type="button" onClick={closeSheet} aria-label={t('common.buttons.close')}><CloseIcon size={18} /></button>
          </header>
          <label className="create-post-feeling-search">
            <SearchIcon size={20} />
            <input
              type="search"
              value={feelingSearch}
              onChange={event => setFeelingSearch(event.target.value)}
              placeholder={t('home.createPost.feeling.searchPlaceholder')}
              aria-label={t('home.createPost.feeling.searchPlaceholder')}
              autoFocus
            />
          </label>
          <div className="create-post-sheet__feelings">
            {filteredFeelings.map(item => (
              <button
                type="button"
                key={item.key}
                className={selectedFeeling === item.key ? 'selected' : ''}
                aria-pressed={selectedFeeling === item.key}
                onClick={() => handleSelectFeeling(item.key)}
              >
                <span aria-hidden="true">{item.emoji}</span>
                {t(`home.createPost.feeling.${item.key}`)}
              </button>
            ))}
          </div>
          {filteredFeelings.length === 0 && (
            <p className="create-post-sheet__empty">
              {t('home.createPost.feeling.noResults')}
            </p>
          )}
          <p className="create-post-sheet__note">{t('home.createPost.feeling.subtitle')}</p>
        </section>
      )}
      {activeSheet === 'hashtags' && (
        <section className="create-post-sheet create-post-sheet--panel" role="dialog" aria-modal="true" aria-labelledby="create-post-hashtags-title">
          <header className="create-post-sheet__header">
            <button className="create-post-sheet__back" type="button" onClick={closeSheet} aria-label={t('common.buttons.back')}><ChevronLeftIcon size={20} /></button>
            <h2 id="create-post-hashtags-title">{t('home.createPost.hashtags.title')}</h2>
            <button className="create-post-sheet__close" type="button" onClick={closeSheet} aria-label={t('common.buttons.close')}><CloseIcon size={18} /></button>
          </header>
          <div className="create-post-tags__panel">
            {selectedHashtags.length > 0 && (
              <ul className="create-post-tags__selected" aria-label={t('home.createPost.addTagsLabel')}>
                {selectedHashtags.map(tag => (
                  <li key={tag}>
                    <button type="button" onClick={() => handleRemoveHashtag(tag)} aria-label={t('home.createPost.hashtags.removeCustom', { tag })}>
                      #{tag}
                      <CloseIcon size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <form className="create-post-tags__form" onSubmit={event => { event.preventDefault(); submitHashtag(); }}>
              <span className="create-post-tags__hash" aria-hidden="true">#</span>
              <input
                value={hashtagInput}
                onChange={event => setHashtagInput(event.target.value)}
                placeholder={t('home.createPost.hashtags.inputPlaceholder')}
                aria-label={t('home.createPost.hashtags.inputPlaceholder')}
                autoFocus
              />
              <button className="sr-only" type="submit">{t('home.createPost.hashtags.addCustom', { tag: hashtagInput.replace(/^#+/, '') })}</button>
            </form>
          </div>
          <div className="create-post-tags__suggested">
            <p>{t('home.createPost.hashtags.suggestedTitle')}</p>
            <div className="create-post-tags__suggestions">
              {SUGGESTED_HASHTAGS.filter(tag => !selectedHashtags.includes(tag)).map(tag => (
                <button type="button" key={tag} onClick={() => handleAddHashtag(tag)} aria-label={t('home.createPost.hashtags.addCustom', { tag })}>
                  #{tag}
                  <PlusIcon size={14} />
                </button>
              ))}
            </div>
          </div>
          <Button className="create-post-sheet__done" fullWidth size="lg" onClick={closeSheet}>{t('home.createPost.hashtags.done')}</Button>
        </section>
      )}
      {activeSheet === 'gif' && (
        <section className="create-post-sheet create-post-sheet--panel create-post-sheet--gif" role="dialog" aria-modal="true" aria-labelledby="create-post-gif-title">
          <header className="create-post-sheet__header">
            <button className="create-post-sheet__back" type="button" onClick={closeSheet} aria-label={t('common.buttons.back')}><ChevronLeftIcon size={20} /></button>
            <h2 id="create-post-gif-title">{t('individualChat.attachment.gif')}</h2>
            <button className="create-post-sheet__close" type="button" onClick={closeSheet} aria-label={t('common.buttons.close')}><CloseIcon size={18} /></button>
          </header>
          <div className="create-post-sheet__gif-panel">
            <GifPicker onSelect={handleGifSelect} showEmptyHint />
          </div>
        </section>
      )}
      {cropOpen && media?.mediaType === POST_MEDIA_TYPE.IMAGE && (
        <div className="create-post-dialog__backdrop">
          <section className="create-post-dialog create-post-crop" role="dialog" aria-modal="true" aria-labelledby="crop-image-title">
            <header className="create-post-crop__header">
              <div><h2 id="crop-image-title">{t('home.createPost.media.crop')}</h2><p>{t('home.createPost.media.cropSubtitle')}</p></div>
              <button type="button" onClick={() => setCropOpen(false)} disabled={cropping} aria-label={t('common.buttons.close')}><CloseIcon size={18} /></button>
            </header>
            <div className="create-post-crop__layout">
              <div className="create-post-crop__preview">
                <ReactCrop circularCrop={cropShape === 'circle'} crop={crop} aspect={cropAspectValue} onChange={(_, percentCrop) => setCrop(percentCrop)}>
                  <img
                    key={requestCropImageCors ? 'cors' : 'plain'}
                    ref={cropImageRef}
                    src={media.uri}
                    alt={t('home.createPost.media.preview')}
                    crossOrigin={requestCropImageCors ? 'anonymous' : undefined}
                    onLoad={handleCropImageLoad}
                    onError={() => { if (requestCropImageCors) setCropCorsFallback(true); }}
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                      height: 'auto',
                      display: 'block',
                    }}
                  />
                </ReactCrop>
              </div>
              <aside className="create-post-crop__controls">
                <h3>{t('home.createPost.media.crop')}</h3>
                <p className="create-post-crop__hint">{t('home.createPost.media.cropHint')}</p>
                <fieldset><legend>{t('home.createPost.media.aspectRatio')}</legend><div className="create-post-crop__aspects">
                  {(['original', 'square', 'portrait'] as const).map(aspect => (
                    <button
                      type="button"
                      className={`${effectiveCropAspect === aspect ? 'selected' : ''}${cropShape === 'circle' && aspect !== 'square' ? ' disabled' : ''}`.trim()}
                      key={aspect}
                      onClick={() => changeCropAspect(aspect)}
                      disabled={cropShape === 'circle' && aspect !== 'square'}
                      aria-pressed={effectiveCropAspect === aspect}
                    >
                      {t(`home.createPost.media.aspect.${aspect}`)}
                    </button>
                  ))}
                </div></fieldset>
                <fieldset><legend>{t('home.createPost.media.cropShape')}</legend><div className="create-post-crop__shapes">
                  {(['rectangle', 'circle'] as const).map(shape => (
                    <button
                      type="button"
                      className={cropShape === shape ? 'selected' : ''}
                      key={shape}
                      onClick={() => {
                        setCropShape(shape);
                        if (shape === 'circle') {
                          setCropAspect('square');
                          if (cropImageRef.current) {
                            setCrop(getInitialCrop(cropImageRef.current, 1));
                          }
                        }
                      }}
                      aria-pressed={cropShape === shape}
                    >
                      {t(`home.createPost.media.shape.${shape}`)}
                    </button>
                  ))}
                </div></fieldset>
              </aside>
            </div>
            <footer className="create-post-crop__footer">
              <button type="button" onClick={() => setCropOpen(false)} disabled={cropping}>{t('common.buttons.cancel')}</button>
              <button type="button" onClick={() => { applyCrop().catch(() => undefined); }} disabled={cropping}>{cropping ? t('common.buttons.saving') : t('home.createPost.media.applyCrop')}</button>
            </footer>
          </section>
        </div>
      )}
      {showDiscardModal && (
        <div className="create-post-dialog__backdrop"><section className="create-post-dialog" role="dialog" aria-modal="true" aria-labelledby="discard-post-title"><h2 id="discard-post-title">{t('home.createPost.discardModal.title')}</h2><p>{t('home.createPost.discardModal.message')}</p><div><button type="button" onClick={handleKeepEditing}>{t('home.createPost.discardModal.keepEditing')}</button><button type="button" onClick={discardAndLeave}>{t('home.createPost.discardModal.discard')}</button></div></section></div>
      )}
    </main>
  );
}